import React, { useState, useMemo } from 'react';
import { MenuItem } from '../redux/slices/menuItemsSlice';
import { parsePrice } from '../utils/priceUtils';
import { useTpnCart } from '../hooks/useTpnCart';
import MenuItemCard from './MenuItemCard';
import BasicMenuItemCard from './BasicMenuItemCard';
import ClassicMenuItemCard from './ClassicMenuItemCard';
import PrimeMenuItemCard from './PrimeMenuItemCard';

export type MenuItemCardVariant = 'basic' | 'classic' | 'modern' | 'prime';

interface MenuGridProps {
  menuItems: MenuItem[];
  selectedCategory: string | null;
  searchQuery?: string;
  variant?: MenuItemCardVariant;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

const MenuGrid: React.FC<MenuGridProps> = ({
  menuItems,
  selectedCategory,
  searchQuery = '',
  variant = 'modern',
  onItemClick,
  onAddToCart,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'category'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Use comprehensive cart visibility logic (includes payment_perferences, restaurant hours, TPN, tables, etc.)
  const shouldShowAddToCart = useTpnCart(true);

  // Filter and sort menu items
  const filteredAndSortedItems = useMemo(() => {
    // Apply same availability filtering as MenuItemCard - show out-of-stock items
    let filtered = menuItems.filter(item => 
      item.is_enabled === true &&
      item.is_item_verified === true
    );

    // Filter by category (support both main categories and subcategories)
    if (selectedCategory) {
      filtered = filtered.filter(item => 
        item.level1_category === selectedCategory || 
        item.category === selectedCategory ||
        item.level2_category === selectedCategory
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query) ||
        item.level1_category?.toLowerCase().includes(query) ||
        item.level2_category?.toLowerCase().includes(query) ||
        item.ingredients?.some(ingredient => 
          ingredient?.toLowerCase().includes(query)
        )
      );
    }

    // Sort items
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'price':
          aValue = parsePrice(a.price);
          bValue = parsePrice(b.price);
          break;
        case 'category':
          aValue = a.level1_category || a.category;
          bValue = b.level1_category || b.category;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [menuItems, selectedCategory, searchQuery, sortBy, sortOrder]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, MenuItem[]> = {};
    
    filteredAndSortedItems.forEach(item => {
      const categoryKey = item.level1_category || item.category || 'Other';
      if (!groups[categoryKey]) {
        groups[categoryKey] = [];
      }
      groups[categoryKey].push(item);
    });

    return groups;
  }, [filteredAndSortedItems]);

  // Get sorted category names
  const categoryNames = useMemo(() => {
    return Object.keys(groupedItems).sort((a, b) => a.localeCompare(b));
  }, [groupedItems]);

  const handleSortChange = (newSortBy: 'name' | 'price' | 'category') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  if (filteredAndSortedItems.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🍽️</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No items found</h3>
        <p className="text-gray-600">
          {searchQuery ? (
            <>No items match your search for "{searchQuery}"</>
          ) : selectedCategory ? (
            <>No items found in "{selectedCategory}" category</>
          ) : (
            <>No menu items available</>
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Sort Controls */}
      {/* <div className="flex items-center justify-between"> */}
        {/* <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">
            {filteredAndSortedItems.length} item{filteredAndSortedItems.length !== 1 ? 's' : ''}
            {selectedCategory && ` in ${selectedCategory}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </span>
        </div> */}

        <div className="flex items-center space-x-2">
          {/* <span className="text-sm text-gray-600">Sort by:</span>
          <div className="flex space-x-1">
            {[
              { key: 'name', label: 'Name' },
              { key: 'price', label: 'Price' },
              { key: 'category', label: 'Category' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleSortChange(key as 'name' | 'price' | 'category')}
                className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                  sortBy === key
                    ? 'bg-orange-100 text-orange-800 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
                {sortBy === key && (
                  <span className="ml-1">
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ))}
          </div> */}
        </div>
      {/* </div> */}

      {/* Menu Items Grid with Category Headers */}
      <div className="space-y-8">
        {categoryNames.map((categoryName) => (
          <div key={categoryName} className="space-y-4">
            {/* Category Header */}
            <div className="pb-2">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                {categoryName}
              </h2>
            </div>

            {/* Items Grid */}
            <div className={`grid gap-6 ${
              variant === 'basic' 
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : variant === 'prime'
                ? 'grid-cols-1 lg:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {groupedItems[categoryName].map((item) => {
                const cardProps = {
                  key: item.pk_id,
                  item,
                  onItemClick,
                  onAddToCart,
                  showAddToCart: shouldShowAddToCart,
                  cartItems,
                  onUpdateQuantity,
                  onIncrementQuantity,
                  onDecrementQuantity,
                };

                switch (variant) {
                  case 'basic':
                    return <BasicMenuItemCard {...cardProps} />;
                  case 'classic':
                    return <ClassicMenuItemCard {...cardProps} />;
                  case 'prime':
                    return <PrimeMenuItemCard {...cardProps} />;
                  case 'modern':
                  default:
                    return <MenuItemCard {...cardProps} />;
                }
              })}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default MenuGrid;
